import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { AuthService, AuthTokenError } from './auth.service';
import { isSignalRUrl, validateAuthConfiguration } from './auth-config';
import { environment } from '../../environments/environment';
import { Store } from '@ngrx/store';
import {
  addNotification,
  resetNotifications,
} from '../../modules/navbar/components/notification-panel/store/notifications.actions';
import {
  NotificationChangeType,
  NotificationInterface,
  NotificationType,
} from '../interfaces';
import { ActivatedRoute, Router } from '@angular/router';
import {
  fetchActiveControls,
  fetchControlsByPolicyId,
  fetchActivePolicies,
  fetchPoliciesCoverage,
  fetchSecureProgram,
  fetchControlDetails,
  fetchTasksCoverage,
  fetchPolicyDetails,
} from 'projects/diso/src/app/modules/security-program/store/security-program.actions';
import {
  fetchComplianceCoverage,
  fetchContractualCompliance,
  fetchCriticalSystems,
  fetchRiskCoverage,
  fetchSecurityPlans,
  fetchLocalLawsProtection,
  fetchSystemSecurityScore,
  fetchSecurityPlanNames,
} from 'projects/diso/src/app/modules/security-plan/store/security-plan.actions';
import {
  fetchTasks,
  fetchTaskStatuses,
  fetchTaskDetails,
} from '../../modules/tasks/store/tasks.actions';
import {
  getShowSidebar,
  getSelectedTaskId,
} from '../../modules/tasks/store/tasks.selectors';
import {
  fetchRiskSimulation,
  fetchRiskSimulationContractualCompliance,
  fetchRiskSimulationProjectedScore,
  fetchRiskSimulationRegulatoryCompliance,
  fetchRiskSimulationRiskBudget,
  fetchRiskSimulationRiskCoverage,
  fetchRiskSimulationLocalLawsCompliance,
} from 'projects/diso/src/app/modules/risk/store/risks.actions';
import { combineLatest, EMPTY, Subject } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import {
  fetchRemediationSolutionDetails,
  fetchToolsAndServicesOverview,
  integrationStatusNotificationReceived,
} from 'projects/diso/src/app/modules/tools-and-services/store/tools-services.actions';
import {
  fetchActionDetails,
  fetchProcedureDetails,
  loadProcedureHeader,
  loadProcedureItems,
  loadProcedures,
} from 'projects/diso/src/app/modules/policies-and-procedures/store/procedures.actions';
import {
  selectActionDetails,
  selectActionItemSidebarIsOpen,
  selectProcedureDetails,
  selectProcedureSidebarIsOpen,
} from 'projects/diso/src/app/modules/policies-and-procedures/store/procedures.selectors';
import { fetchSidebarWarnings } from '../../modules/sidebar/store/sidebar.actions';
import { refreshSecurityScoreViews } from 'projects/diso/src/app/modules/security-program/modules/security-score/store/security-score.actions';
import { fetchActiveRisks } from 'projects/diso/src/app/modules/security-program/modules/active-risks/store/active-risks.actions';
import {
  fetchAssetDetailsForSidebar,
  setNeedUpdate,
} from 'projects/diso/src/app/modules/cyber-assets/store/cyber-assets.actions';
import { getSelectedAssetId } from 'projects/diso/src/app/modules/cyber-assets/store/cyber-assets.selectors';
import {
  fetchHomeSystemSecurityScore,
  fetchHomeTaskDetails,
  fetchHomeTasks,
} from 'projects/diso/src/app/modules/home/store/home.actions';
import {
  getHomeSelectedTaskId,
  getHomeTasksShowSidebar,
} from 'projects/diso/src/app/modules/home/store/home.selectors';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private _url = environment.streamUrl;
  private _hubConnection!: signalR.HubConnection;
  private _connectionInflight: Promise<void> | null = null;
  private _connectionGeneration = 0;
  private _recentUserActions: Set<string> = new Set(); // Track recent user actions
  public connectionId: string = '';
  private readonly procedureChangedSubject = new Subject<string>();
  private readonly procedureItemsChangedSubject = new Subject<string>();
  private readonly riskMappedSubject = new Subject<{ riskInOrgId: string }>();
  private readonly actionMappingChangedSubject = new Subject<{
    actionMappingId: string;
    planId: string;
  }>();
  private readonly controlActionCandidatesChangedSubject = new Subject<void>();

  procedureChanged$ = this.procedureChangedSubject.asObservable();
  procedureItemsChanged$ = this.procedureItemsChangedSubject.asObservable();
  riskMapped$ = this.riskMappedSubject.asObservable();
  actionMappingChanged$ = this.actionMappingChangedSubject.asObservable();
  controlActionCandidatesChanged$ =
    this.controlActionCandidatesChangedSubject.asObservable();

  constructor(
    private _authService: AuthService,
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this._authService.sessionEnded$.subscribe(() => {
      void this.stopConnection().catch(() => {
        this.connectionId = '';
      });
    });
  }

  public startConnection = (): Promise<void> => {
    if (this._connectionInflight) return this._connectionInflight;
    if (
      this._hubConnection?.state === signalR.HubConnectionState.Connected ||
      this._hubConnection?.state === signalR.HubConnectionState.Reconnecting
    ) {
      return Promise.resolve();
    }
    this._connectionInflight = this.connect().finally(() => {
      this._connectionInflight = null;
    });
    return this._connectionInflight;
  };

  private async connect(): Promise<void> {
    validateAuthConfiguration();
    if (!isSignalRUrl(this._url))
      throw new Error('Unconfigured SignalR endpoint');
    const generation = this._connectionGeneration;
    if (this._hubConnection) await this._hubConnection.stop();
    await this._authService.ensureApiBearer();
    if (generation !== this._connectionGeneration)
      throw new AuthTokenError('session_ended');
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(this._url, {
        accessTokenFactory: () => {
          if (generation !== this._connectionGeneration) {
            return Promise.reject(new AuthTokenError('session_ended'));
          }
          return this._authService.ensureApiBearer();
        },
      })
      .withAutomaticReconnect()
      .build();
    this._hubConnection = connection;
    connection.onreconnecting(() => {
      if (this._hubConnection === connection) this.connectionId = '';
    });
    connection.onclose(() => {
      if (this._hubConnection === connection) this.connectionId = '';
    });
    connection.onreconnected((newId) => {
      if (
        this._hubConnection === connection &&
        generation === this._connectionGeneration
      ) {
        this.connectionId = newId ?? '';
      }
    });
    try {
      await connection.start();
      if (generation !== this._connectionGeneration) {
        await connection.stop();
        throw new AuthTokenError('session_ended');
      }
      this.connectionId = connection.connectionId ?? '';
    } catch (error) {
      this.connectionId = '';
      throw error;
    }
  }

  public async stopConnection(): Promise<void> {
    this._connectionGeneration++;
    this.connectionId = '';
    if (this._hubConnection) await this._hubConnection.stop();
  }

  public addNotificationsListener = () => {
    if (!this._hubConnection) return;
    this._hubConnection.on('DomainDataChanged', (notification) => {
      console.log('Notification received', notification);
      if (
        notification.apiToCall.length &&
        notification.actorConnectionId !== this.connectionId
      ) {
        // Security Program overview: its APIs are reported together, so
        // refresh the whole page once rather than once per API.
        if (
          notification.apiToCall.some(
            (api: string) =>
              api.includes('CMDISOSecurityProgramOverview/') ||
              api.includes('CMDISODome/Overview'),
          )
        ) {
          this.store.dispatch(refreshSecurityScoreViews());
        }
        notification.apiToCall.forEach((api: string) => {
          if (api.includes('CMDISOTasks/GetActiveTasksCoverage')) {
            this.store.dispatch(fetchTasksCoverage({ bypassLoader: true }));
          }
          if (
            api.includes('CMDISOControls/GetDetailsForSidebar') &&
            notification.affectedIds?.controlId
          ) {
            this.store.dispatch(
              fetchControlDetails({
                controlId: notification.affectedIds?.controlId,
                bypassLoader: true,
              }),
            );
          }
          if (api.includes('CMDISOSecurityProgramControls/GetActiveControls')) {
            this.store.dispatch(
              fetchActiveControls({
                bypassLoader: true,
              }),
            );
          }
          if (api.includes('CMDISOAssets/GetAssets')) {
            this.store.dispatch(
              setNeedUpdate({
                needUpdate: true,
              }),
            );
          }
          if (api.includes('CMDISOAssets/GetAssetDetailsForSidebar')) {
            this.store
              .select(getSelectedAssetId)
              .pipe(
                take(1),
                catchError(() => EMPTY),
              )
              .subscribe((assetId) => {
                if (assetId) {
                  this.store.dispatch(
                    fetchAssetDetailsForSidebar({
                      assetId,
                      bypassLoader: true,
                    }),
                  );
                }
              });
          }
          if (api.includes('CMDISOSecurityPlan/GetPlanNames')) {
            this.store.dispatch(fetchSecurityPlanNames());
          }
          if (api.includes('CMDISORiskInOrg/Dashboard')) {
            this.store.dispatch(
              fetchActiveRisks({
                bypassLoader: true,
              }),
            );
          }
          if (api.includes('CMDISOSecurityProgram/GetSystemSecurityScore')) {
            this.store.dispatch(
              fetchSystemSecurityScore({
                bypassLoader: true,
              }),
            );
            this.store.dispatch(
              fetchHomeSystemSecurityScore({ bypassLoader: true }),
            );
          }
          if (
            api.includes(
              'CMDISOSecurityProgram/GetSensitiveDataProtectionCoverage',
            ) &&
            !api.includes(
              'CMDISOSecurityProgram/GetSensitiveDataProtectionCoverageForPlan',
            )
          ) {
            this.store.dispatch(
              fetchLocalLawsProtection({
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISOSecurityProgram/GetCriticalSystemsCoverage')
          ) {
            this.store.dispatch(
              fetchCriticalSystems({
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISOSecurityProgram/CalculateComplianceCoverage')
          ) {
            this.store.dispatch(
              fetchComplianceCoverage({
                bypassLoader: true,
              }),
            );
          }
          if (api.includes('CMDISORiskInOrg/GetRiskCoverageBarData')) {
            this.store.dispatch(
              fetchRiskCoverage({
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes(
              'CMDISOSecurityProgram/CalculateContractualCompliance',
            ) &&
            !api.includes(
              'CMDISOSecurityProgram/CalculateContractualComplianceForPlan',
            )
          ) {
            this.store.dispatch(
              fetchContractualCompliance({
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISORiskInOrg/DetectedRisks') &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulation({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISOSecurityProgram/GetProjectedSystemScore') &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulationProjectedScore({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISORiskInOrg/CalculateRiskCoverageForPlan') &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulationRiskCoverage({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISOSecurityPlan/GetRemainingBudget') &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulationRiskBudget({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes(
              'CMDISOSecurityProgram/CalculateContractualComplianceForPlan',
            ) &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulationContractualCompliance({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISOSecurityProgram/CalculateRegulatoryCoverage') &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulationRegulatoryCompliance({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (api.includes('CMDISOProcedure/{procedureId}/items')) {
            const idFromUrl = this.getProcedureIdFromRoute();
            if (idFromUrl) {
              this.store.dispatch(
                loadProcedureItems({
                  procedureId: idFromUrl,
                  bypassLoader: true,
                }),
              );
            }
          }
          if (
            api.includes('CMDISOPolicy/GetPolicyDetailsForSidebar') &&
            notification.affectedIds?.policyId
          ) {
            this.store.dispatch(
              fetchPolicyDetails({
                skipOpenIdCheck: true,
                policyId: notification.affectedIds.policyId,
                bypassLoader: true,
              }),
            );
          }
          if (
            api.includes('CMDISOProcedure/{procedureId}/procedureDetails') ||
            (notification.affectedIds?.procedureId &&
              api.includes(
                `CMDISOProcedure/${notification.affectedIds.procedureId}/procedureDetails`,
              ))
          ) {
            combineLatest([
              this.store.select(selectProcedureSidebarIsOpen),
              this.store.select(selectProcedureDetails),
            ])
              .pipe(
                take(1),
                catchError(() => EMPTY),
              )
              .subscribe(([isOpen, details]) => {
                const openProcedureId = details?.procedureId;
                if (
                  isOpen &&
                  openProcedureId &&
                  (!notification.affectedIds?.procedureId ||
                    notification.affectedIds.procedureId === openProcedureId)
                ) {
                  this.store.dispatch(
                    fetchProcedureDetails({
                      procedureId: openProcedureId,
                      bypassLoader: true,
                    }),
                  );
                }
              });
          }
          if (
            api.includes('CMDISOProcedure/{itemId}/actionDetails') &&
            notification.affectedIds?.itemId
          ) {
            combineLatest([
              this.store.select(selectActionItemSidebarIsOpen),
              this.store.select(selectActionDetails),
            ])
              .pipe(
                take(1),
                catchError(() => EMPTY),
              )
              .subscribe(([isOpen, details]) => {
                const openItemId = details?.activeProcedureItemId;
                if (
                  isOpen &&
                  openItemId &&
                  openItemId === notification.affectedIds?.itemId
                ) {
                  this.store.dispatch(
                    fetchActionDetails({
                      activeProcedureItemId: openItemId,
                      bypassLoader: true,
                    }),
                  );
                }
              });
          }
          if (
            api.includes(
              'CMDISOSecurityProgram/GetSensitiveDataProtectionCoverageForPlan',
            ) &&
            notification.affectedIds?.planId
          ) {
            this.store.dispatch(
              fetchRiskSimulationLocalLawsCompliance({
                planId: notification.affectedIds.planId,
                bypassLoader: true,
              }),
            );
          }
          if (api.includes('CMDISOTasks/GetStatuses')) {
            this.store.dispatch(fetchTaskStatuses({ bypassLoader: true }));
          }
          if (api.includes('CMDISOTasks/GetUsersTasks')) {
            this.store.dispatch(fetchTasks({ bypassLoader: true }));
          }
          if (api.includes('CMDISOTasks/GetUsersTaskForHomepage')) {
            this.store.dispatch(fetchHomeTasks({ bypassLoader: true }));
          }
          if (api.includes('CMDISOTasks/GetDetailsForSidebar')) {
            combineLatest([
              this.store.select(getShowSidebar),
              this.store.select(getSelectedTaskId),
            ])
              .pipe(
                take(1),
                catchError(() => EMPTY),
              )
              .subscribe(([isOpen, selectedTaskId]) => {
                if (
                  isOpen &&
                  selectedTaskId &&
                  (!notification.affectedIds?.taskId ||
                    notification.affectedIds.taskId === selectedTaskId)
                ) {
                  this.store.dispatch(
                    fetchTaskDetails({
                      taskId: selectedTaskId,
                      bypassLoader: true,
                    }),
                  );
                }
              });
            combineLatest([
              this.store.select(getHomeTasksShowSidebar),
              this.store.select(getHomeSelectedTaskId),
            ])
              .pipe(
                take(1),
                catchError(() => EMPTY),
              )
              .subscribe(([isOpen, selectedTaskId]) => {
                if (
                  isOpen &&
                  selectedTaskId &&
                  (!notification.affectedIds?.taskId ||
                    notification.affectedIds.taskId === selectedTaskId)
                ) {
                  this.store.dispatch(
                    fetchHomeTaskDetails({
                      taskId: selectedTaskId,
                      bypassLoader: true,
                    }),
                  );
                }
              });
          }
          if (
            api.includes('CMDISOControlTemplates/LoadControlActionCandidates')
          ) {
            this.controlActionCandidatesChangedSubject.next();
          }
          if (
            api.includes('CMDISOControlForPolicy/ControlsForPolicy') &&
            notification.affectedIds?.policyId
          ) {
            this.store.dispatch(
              fetchControlsByPolicyId({
                policyId: notification.affectedIds.policyId,
                bypassLoader: true,
              }),
            );
          }
          if (
            notification.changeType === NotificationChangeType.RiMSChanged &&
            notification.affectedIds?.changedActionId &&
            (notification.affectedIds?.riskInOrgId ||
              notification.affectedIds?.riskInPlanId)
          ) {
            this.riskMappedSubject.next({
              riskInOrgId:
                notification.affectedIds.riskInOrgId ||
                notification.affectedIds.riskInPlanId,
            });
          }
          if (
            notification.changeType ===
              NotificationChangeType.SolutionMapping &&
            notification.affectedIds?.changedActionId &&
            notification.affectedIds?.planId
          ) {
            this.actionMappingChangedSubject.next({
              actionMappingId: notification.affectedIds.changedActionId,
              planId: notification.affectedIds.planId,
            });
          }
          if (api.includes('CMDISOSecurityProgram/GetPlansList')) {
            this.store.dispatch(fetchSecurityPlans({ bypassLoader: true }));
          }
          if (api.includes('CMDISOCustomerRemediationSolution/overview')) {
            this.store.dispatch(
              fetchToolsAndServicesOverview({ bypassLoader: true }),
            );
          }
          if (api.includes('CMDISOPolicy/GetPolicyCoverage')) {
            this.store.dispatch(fetchPoliciesCoverage({ bypassLoader: true }));
          }
          if (api.includes('CMDISOPolicy/GetActivePolicies')) {
            this.store.dispatch(fetchActivePolicies({ bypassLoader: true }));
          }
          if (
            api === 'api/CMDISOProcedure' &&
            notification.affectedIds?.procedureId
          ) {
            const procedureId =
              this.getProcedureIdFromRoute() ||
              notification.affectedIds?.procedureId;
            this.store.dispatch(
              loadProcedureHeader({
                procedureId,
                bypassLoader: true,
              }),
            );
            this.store.dispatch(loadProcedures({ bypassLoader: true }));
          }
          if (
            /CMDISOProcedure\/[^/]+\/items/.test(api) &&
            notification.affectedIds?.procedureId
          ) {
            const procedureId =
              this.getProcedureIdFromRoute() ||
              notification.affectedIds?.procedureId;
            this.store.dispatch(
              loadProcedureItems({
                procedureId,
                bypassLoader: true,
              }),
            );
          }
          if (
            notification.affectedIds?.toolId &&
            (api.includes(
              `CMDISOCustomerRemediationSolution/{solutionId}/view`,
            ) ||
              api.includes(
                `CMDISOCustomerRemediationSolution/${notification.affectedIds.toolId}/view`,
              ))
          ) {
            this.store.dispatch(
              fetchRemediationSolutionDetails({
                solutionId: notification.affectedIds.toolId,
                bypassLoader: true,
              }),
            );
          }
        });
      }
      // Deliberately outside the actorConnectionId guard above: the integration status
      // changes as a result of *this* user's connect request, so the notification may well
      // carry this client's connection id — and a sub-sidebar frozen on that request is
      // waiting for exactly this signal.
      if (
        notification.apiToCall?.some(
          (api: string) =>
            api.includes('CMDISOCustomerRemediationSolution') ||
            api.includes('CMDISOIntegrations'),
        )
      ) {
        this.store.dispatch(
          integrationStatusNotificationReceived({
            solutionId: notification.affectedIds?.toolId,
            messageId: notification.affectedIds?.messageId,
          }),
        );
      }
      if (
        notification.apiToCall?.some((api: string) =>
          api.includes('CMDISOWarnings/warnings'),
        )
      ) {
        this.store.dispatch(fetchSidebarWarnings());
      }
      if (
        notification.apiToCall?.some((api: string) =>
          api.includes('CMDISOSecurityProgramControls/securityProgram/boxes'),
        )
      ) {
        this.store.dispatch(fetchSecureProgram({ bypassLoader: true }));
      }
    });
    this._hubConnection.on(
      'NotifyToBellNotification',
      (notification: NotificationInterface) => {
        console.log('Notification received:', notification);

        // Only refresh if this notification is not from the current user's action
        if (!this.isRecentUserAction(notification)) {
          this.store.dispatch(addNotification({ notification }));
          this.refreshCurrentPageData(notification);
        }
      },
    );

    this._hubConnection.on(
      'ProcedureChanged',
      (payload: { procedureId?: string }) => {
        if (payload?.procedureId) {
          this.procedureChangedSubject.next(payload.procedureId);
        }
      },
    );

    this._hubConnection.on(
      'ProcedureItemsChanged',
      (payload: { procedureId?: string }) => {
        if (payload?.procedureId) {
          this.procedureItemsChangedSubject.next(payload.procedureId);
        }
      },
    );
  };

  // Method to track user actions that will generate notifications
  public trackUserAction(actionId: string): void {
    this._recentUserActions.add(actionId);
    // Remove the action ID after a short delay
    setTimeout(() => {
      this._recentUserActions.delete(actionId);
    }, 5000);
  }

  private getProcedureIdFromRoute(): string | null {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('procedureId');
  }

  // Check if a notification is related to a recent user action
  private isRecentUserAction(notification: NotificationInterface): boolean {
    return (
      this._recentUserActions.has(notification.id || '') ||
      this._recentUserActions.has(
        `${notification.notificationType}_${notification.id}`,
      ) ||
      this._recentUserActions.has(`${notification.notificationType}_create`)
    );
  }

  public refreshCurrentPageData(notification: NotificationInterface): void {
    const currentUrl = this.router.url;

    // Determine if we need to refresh data based on current page and notification type
    if (
      currentUrl.includes('/homePage') &&
      notification.notificationType === NotificationType.DisoTasks
    ) {
      this.store.dispatch(fetchHomeTasks({ bypassLoader: true }));
    } else if (
      currentUrl.includes('/pendingTasks') &&
      notification.notificationType === NotificationType.DisoTasks
    ) {
      this.store.dispatch(fetchTasks({ controlId: '', bypassLoader: true }));
    } else if (
      currentUrl.includes('/risks') &&
      notification.notificationType === NotificationType.Risks
    ) {
      // this.store.dispatch(fetchRisks());
    } else if (
      currentUrl.includes('/policies') &&
      notification.notificationType === NotificationType.Policies
    ) {
      this.store.dispatch(fetchActivePolicies({}));
    } else if (
      currentUrl.includes('/controls') &&
      notification.notificationType === NotificationType.Controls
    ) {
      this.store.dispatch(fetchActiveControls({ bypassLoader: true }));
    } else if (
      currentUrl.includes('/assets') &&
      notification.notificationType === NotificationType.Assets
    ) {
      // this.store.dispatch(fetchAssets());
    } else if (
      currentUrl.includes('/securityPlan') &&
      notification.notificationType === NotificationType.Plans
    ) {
      this.store.dispatch(fetchSecurityPlans({ bypassLoader: true }));
      this.store.dispatch(fetchSystemSecurityScore({ bypassLoader: true }));
    }
  }

  public resetNotifications = (notificationType: NotificationType) => {
    this.store.dispatch(resetNotifications({ notificationType }));
  };
}
